import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next'; // Importazione hook per traduzione
import { toast } from 'react-toastify';
import { 
  Save, 
  User, 
  ArrowLeft, 
  Clock, 
  Dumbbell, 
  Target, 
  AlertTriangle,
  Heart
} from 'lucide-react';
import StyledCard from '../components/ui/StyledCard';
import GradientButton from '../components/ui/GradientButton';
import { useNavigate, Link } from 'react-router-dom';
import SidebarLayout from '../components/layout/SidebarLayout';

const UserProfilePage = () => {
  const { getAuthenticatedApi, currentUser } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation(); // Hook di traduzione
  const navigate = useNavigate();
  const api = getAuthenticatedApi();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    height: 175,
    weight: 70,
    age: 30,
    gender: 'male',
    experienceLevel: 'beginner',
    fitnessGoals: 'general_fitness',
    injuries: '',
    preferences: '',
    notes: ''
  });
  
  // Fetch user profile data on component mount
  useEffect(() => {
    loadUserProfile();
  }, []);
  
  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/utente_profilo.php');
      
      if (response.data && response.data.user_id) {
        // Converte i valori numerici in numeri (in caso arrivino come stringhe)
        const formattedProfile = {
          ...response.data,
          height: response.data.height ? Number(response.data.height) : 175,
          weight: response.data.weight ? Number(response.data.weight) : 70,
          age: response.data.age ? Number(response.data.age) : 30,
        };
        
        // Formatta fitness goals se necessario
        if (formattedProfile.fitnessGoals && typeof formattedProfile.fitnessGoals === 'string') {
          try {
            // Tenta di analizzare come JSON se è in formato JSON
            if (formattedProfile.fitnessGoals.startsWith('[') || formattedProfile.fitnessGoals.startsWith('{')) {
              formattedProfile.fitnessGoals = JSON.parse(formattedProfile.fitnessGoals);
            }
          } catch (e) {
            console.warn('Formato obiettivi fitness non valido:', e);
          }
        }
        
        // Se gli obiettivi fitness sono un array, convertili in stringa separata da virgole
        if (Array.isArray(formattedProfile.fitnessGoals)) {
          formattedProfile.fitnessGoals = formattedProfile.fitnessGoals.join(',');
        }
        
        setProfile(formattedProfile);
      }
    } catch (error) {
      console.error('Errore nel caricamento del profilo:', error);
      toast.error(t('userProfile.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Prepara i dati da inviare
      const dataToSubmit = {
        ...profile,
        // Converti altezza, peso, età in numeri
        height: parseInt(profile.height) || 175,
        weight: parseFloat(profile.weight) || 70,
        age: parseInt(profile.age) || 30,
      };
      
      // Assicuriamoci che i valori enum siano gestiti correttamente
      // Se gender non è definito o non è valido, impostiamolo su 'male'
      if (!['male', 'female', 'other'].includes(dataToSubmit.gender)) {
        dataToSubmit.gender = 'male';
      }
      
      // Se experienceLevel non è definito o non è valido, impostiamolo su 'beginner'
      if (!['beginner', 'intermediate', 'advanced'].includes(dataToSubmit.experienceLevel)) {
        dataToSubmit.experienceLevel = 'beginner';
      }
      
      // Richiesta PUT per aggiornare il profilo
      const response = await api.put('/utente_profilo.php', dataToSubmit);
      
      if (response.data && response.data.message) {
        toast.success(t('userProfile.messages.updateSuccess'));
        
        // Aggiorna il profilo con i dati ricevuti
        if (response.data.profile) {
          setProfile(response.data.profile);
        }
        
        // Ricarica il profilo per assicurarsi che tutti i dati siano aggiornati
        loadUserProfile();
      }
    } catch (error) {
      console.error('Errore durante il salvataggio del profilo:', error);
      toast.error(error.response?.data?.error || t('userProfile.messages.updateError'));
    } finally {
      setSaving(false);
    }
  };
  
  // Helper function to determine proper text color based on theme
  const textColor = isDark ? 'text-gray-300' : 'text-gray-800';
  const labelColor = isDark ? 'text-gray-400' : 'text-gray-600';
  
  const profileContent = () => {
    if (loading) {
      return (
        <div className="max-w-4xl mx-auto p-4">
          <StyledCard>
            <div className="py-8 text-center">
              <div className={`animate-spin h-8 w-8 border-4 rounded-full mx-auto ${isDark ? 'border-blue-500 border-t-transparent' : 'border-blue-600 border-t-transparent'}`}></div>
              <p className="mt-4">{t('userProfile.loading')}</p>
            </div>
          </StyledCard>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto p-4">
        {/* Header with navigation */}
        <div className="flex justify-between items-center mb-6">
          <h1 className={`text-2xl font-bold ${textColor} flex items-center`}>
            <User className="mr-2 h-6 w-6" />
            {t('userProfile.title')}
          </h1>
          
          <Link to="/">
            <GradientButton
              variant="secondary"
              size="sm"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('userProfile.back')}
            </GradientButton>
          </Link>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Personal Info Card */}
            <StyledCard className="col-span-1">
              <StyledCard.Header>
                <StyledCard.Title>
                  <div className="flex items-center">
                    <User className={`h-5 w-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    {t('userProfile.personal.title')}
                  </div>
                </StyledCard.Title>
              </StyledCard.Header>
              <StyledCard.Body>
                <div className="space-y-4">
                  {/* Height */}
                  <div>
                    <label htmlFor="height" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.personal.height')}
                    </label>
                    <input
                      type="number"
                      name="height"
                      id="height"
                      value={profile.height || ''}
                      onChange={handleChange}
                      min="100"
                      max="250"
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    />
                  </div>
                  
                  {/* Weight */}
                  <div>
                    <label htmlFor="weight" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.personal.weight')}
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="weight"
                      id="weight"
                      value={profile.weight || ''}
                      onChange={handleChange}
                      min="30"
                      max="250"
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    />
                  </div>
                  
                  {/* Age */}
                  <div>
                    <label htmlFor="age" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.personal.age')}
                    </label>
                    <input
                      type="number"
                      name="age"
                      id="age"
                      value={profile.age || ''}
                      onChange={handleChange}
                      min="16"
                      max="100"
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    />
                  </div>
                  
                  {/* Gender */}
                  <div>
                    <label htmlFor="gender" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.personal.gender')}
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      value={profile.gender || 'male'}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    >
                      <option value="male">{t('userProfile.personal.genderOptions.male')}</option>
                      <option value="female">{t('userProfile.personal.genderOptions.female')}</option>
                      <option value="other">{t('userProfile.personal.genderOptions.other')}</option>
                    </select>
                  </div>
                </div>
              </StyledCard.Body>
            </StyledCard>
            
            {/* Fitness Experience Card */}
            <StyledCard className="col-span-1">
              <StyledCard.Header>
                <StyledCard.Title>
                  <div className="flex items-center">
                    <Dumbbell className={`h-5 w-5 mr-2 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    {t('userProfile.fitness.title')}
                  </div>
                </StyledCard.Title>
              </StyledCard.Header>
              <StyledCard.Body>
                <div className="space-y-4">
                  {/* Experience Level */}
                  <div>
                    <label htmlFor="experienceLevel" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.fitness.experienceLevel')}
                    </label>
                    <select
                      id="experienceLevel"
                      name="experienceLevel"
                      value={profile.experienceLevel || 'beginner'}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    >
                      <option value="beginner">{t('userProfile.fitness.experienceOptions.beginner')}</option>
                      <option value="intermediate">{t('userProfile.fitness.experienceOptions.intermediate')}</option>
                      <option value="advanced">{t('userProfile.fitness.experienceOptions.advanced')}</option>
                    </select>
                    <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {profile.experienceLevel === 'beginner' && t('userProfile.fitness.experienceDescription.beginner')}
                      {profile.experienceLevel === 'intermediate' && t('userProfile.fitness.experienceDescription.intermediate')}
                      {profile.experienceLevel === 'advanced' && t('userProfile.fitness.experienceDescription.advanced')}
                    </p>
                  </div>
                  
                  {/* Fitness Goals */}
                  <div>
                    <label htmlFor="fitnessGoals" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.fitness.goals')}
                    </label>
                    <select
                      id="fitnessGoals"
                      name="fitnessGoals"
                      value={profile.fitnessGoals || 'general_fitness'}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    >
                      <option value="general_fitness">{t('userProfile.fitness.goalOptions.general_fitness')}</option>
                      <option value="muscle_gain">{t('userProfile.fitness.goalOptions.muscle_gain')}</option>
                      <option value="strength">{t('userProfile.fitness.goalOptions.strength')}</option>
                      <option value="weight_loss">{t('userProfile.fitness.goalOptions.weight_loss')}</option>
                      <option value="endurance">{t('userProfile.fitness.goalOptions.endurance')}</option>
                      <option value="flexibility">{t('userProfile.fitness.goalOptions.flexibility')}</option>
                    </select>
                  </div>
                  
                  {/* Training Preferences */}
                  <div>
                    <label htmlFor="preferences" className={`block text-sm font-medium ${labelColor}`}>
                      {t('userProfile.fitness.preferences')}
                    </label>
                    <textarea
                      id="preferences"
                      name="preferences"
                      rows="3"
                      value={profile.preferences || ''}
                      onChange={handleChange}
                      placeholder={t('userProfile.fitness.preferencesPlaceholder')}
                      className={`mt-1 block w-full rounded-md ${
                        isDark 
                          ? 'bg-gray-800 border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                    />
                  </div>
                </div>
              </StyledCard.Body>
            </StyledCard>
            
            {/* Health & Injuries Card */}
            <StyledCard className="col-span-1">
              <StyledCard.Header>
                <StyledCard.Title>
                  <div className="flex items-center">
                    <Heart className={`h-5 w-5 mr-2 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                    {t('userProfile.health.title')}
                  </div>
                </StyledCard.Title>
              </StyledCard.Header>
              <StyledCard.Body>
                <div>
                  <label htmlFor="injuries" className={`block text-sm font-medium ${labelColor}`}>
                    {t('userProfile.health.injuries')}
                  </label>
                  <textarea
                    id="injuries"
                    name="injuries"
                    rows="4"
                    value={profile.injuries || ''}
                    onChange={handleChange}
                    placeholder={t('userProfile.health.injuriesPlaceholder')}
                    className={`mt-1 block w-full rounded-md ${
                      isDark 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                  />
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('userProfile.health.aiHelpNote')}
                  </p>
                </div>
              </StyledCard.Body>
            </StyledCard>
            
            {/* Notes Card */}
            <StyledCard className="col-span-1">
              <StyledCard.Header>
                <StyledCard.Title>
                  <div className="flex items-center">
                    <Target className={`h-5 w-5 mr-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    {t('userProfile.notes.title')}
                  </div>
                </StyledCard.Title>
              </StyledCard.Header>
              <StyledCard.Body>
                <div>
                  <label htmlFor="notes" className={`block text-sm font-medium ${labelColor}`}>
                    {t('userProfile.notes.label')}
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows="4"
                    value={profile.notes || ''}
                    onChange={handleChange}
                    placeholder={t('userProfile.notes.placeholder')}
                    className={`mt-1 block w-full rounded-md ${
                      isDark 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } shadow-sm focus:border-blue-500 focus:ring-blue-500`}
                  />
                </div>
              </StyledCard.Body>
            </StyledCard>
          </div>
          
          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <GradientButton
              type="submit"
              variant="primary"
              size="default"
              disabled={saving}
              className="flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div>
                  {t('userProfile.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t('userProfile.saveProfile')}
                </>
              )}
            </GradientButton>
          </div>
        </form>
        
        {/* Additional Information */}
        <div className={`mt-8 p-4 rounded-lg border ${
          isDark 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-start">
            <AlertTriangle className={`h-5 w-5 mt-0.5 mr-2 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
            <div>
              <h3 className={`text-sm font-medium ${textColor}`}>
                {t('userProfile.importanceInfo.title')}
              </h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('userProfile.importanceInfo.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Integrazione con MainLayout
  return (
    <SidebarLayout>
      {profileContent()}
    </SidebarLayout>
  );
};

export default UserProfilePage;